import React, { useState } from 'react';
import { Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

const CompactPieChart = ({ title, data, labels, colors, countUnit = 'Count' }) => {
  // State for sort direction (true = descending, false = ascending)
  const [sortDescending, setSortDescending] = useState(true);

  // Handle empty or invalid data
  const validData = Array.isArray(data) && data.length > 0 ? data : [];
  const validLabels = Array.isArray(labels) && labels.length > 0 ? labels : [];
  const validColors = Array.isArray(colors) && colors.length > 0 ? colors : [];
  
  // Check if there's actually data to display (not just empty arrays or all zeros)
  const hasData = validData.length > 0 && validData.some(value => value > 0);
  
  // If no data, show empty state
  if (!hasData) {
    return (
      <div className="compact-pie-chart-container">
        <h4 className="compact-pie-chart-title" style={{ textAlign: 'center', marginBottom: '8px' }}>{title}</h4>
        <div className="blank-chart-placeholder" style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <p>No data available</p>
        </div>
      </div>
    );
  }

  const chartData = {
    labels: validLabels,
    datasets: [{
      data: validData,
      backgroundColor: validColors,
      borderWidth: 1,
      borderColor: '#fff'
    }]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 1, // Force square aspect ratio
    plugins: {
      legend: {
        display: false // We'll create a custom table legend on the side
      },
      tooltip: {
        enabled: false, // Disable default tooltips
        external: function(context) {
          // Custom external tooltip that can render anywhere
          const { chart, tooltip } = context;
          
          // Get or create tooltip element
          let tooltipEl = chart.canvas.parentNode.querySelector('.chartjs-tooltip');
          if (!tooltipEl) {
            tooltipEl = document.createElement('div');
            tooltipEl.className = 'chartjs-tooltip';
            tooltipEl.style.opacity = '0';
            tooltipEl.style.position = 'absolute';
            tooltipEl.style.background = 'rgba(0, 0, 0, 0.8)';
            tooltipEl.style.color = 'white';
            tooltipEl.style.borderRadius = '6px';
            tooltipEl.style.padding = '8px 12px';
            tooltipEl.style.pointerEvents = 'none';
            tooltipEl.style.transform = 'translate(-50%, -100%)';
            tooltipEl.style.transition = 'all 0.2s ease';
            tooltipEl.style.fontSize = '12px';
            tooltipEl.style.fontWeight = '500';
            tooltipEl.style.zIndex = '1000';
            tooltipEl.style.whiteSpace = 'nowrap';
            tooltipEl.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
            chart.canvas.parentNode.appendChild(tooltipEl);
          }

          // Hide if no tooltip
          if (tooltip.opacity === 0) {
            tooltipEl.style.opacity = '0';
            return;
          }

          // Set content
          if (tooltip.body) {
            const bodyLines = tooltip.body.map(b => b.lines);
            const total = tooltip.dataPoints[0].dataset.data.reduce((acc, val) => acc + val, 0);
            const value = tooltip.dataPoints[0].raw;
            const percentage = Math.round((value / total) * 100);
            const label = tooltip.dataPoints[0].label;
            
            tooltipEl.innerHTML = `${label}: ${value} (${percentage}%)`;
          }

          // Position tooltip relative to the entire container, not just the canvas
          const containerRect = chart.canvas.parentNode.parentNode.getBoundingClientRect();
          const canvasRect = chart.canvas.getBoundingClientRect();
          
          // Calculate position relative to the container
          const left = tooltip.caretX + (canvasRect.left - containerRect.left);
          const top = tooltip.caretY + (canvasRect.top - containerRect.top) - 10;
          
          tooltipEl.style.opacity = '1';
          tooltipEl.style.left = left + 'px';
          tooltipEl.style.top = top + 'px';
        }
      }
    },
    layout: {
      padding: 0
    },
    interaction: {
      intersect: false,
      mode: 'nearest'
    }
  };

  const total = validData.reduce((acc, val) => acc + val, 0);

  // Sort data by percentage based on sort direction
  const sortedIndices = validData
    .map((value, index) => ({
      index,
      percentage: (value / total) * 100
    }))
    .sort((a, b) => sortDescending ? b.percentage - a.percentage : a.percentage - b.percentage)
    .map(item => item.index);

  // Create sorted arrays
  const sortedLabels = sortedIndices.map(i => validLabels[i]);
  const sortedData = sortedIndices.map(i => validData[i]);
  const sortedColors = sortedIndices.map(i => validColors[i]);

  // Format the count unit label
  const countUnitLabel = (countUnit !== 'Count') ? `Value (${countUnit})` : countUnit;

  // Toggle sort direction
  const handleSortToggle = () => {
    setSortDescending(!sortDescending);
  };

  return (
    <div className="compact-pie-chart-container">
      <div className="compact-pie-chart-content">
        <div className="compact-pie-chart-left">
          <h4 className="compact-pie-chart-title">{title}</h4>
          <div className="compact-pie-chart-wrapper">
            <Pie data={chartData} options={options} />
          </div>
        </div>
        <div className="compact-pie-chart-table">
          <div className="compact-pie-table-header">
            <span>Category</span>
            <span>{countUnitLabel}</span>
            <span 
              onClick={handleSortToggle} 
              style={{ cursor: 'pointer', userSelect: 'none' }}
              title={`Click to sort ${sortDescending ? 'ascending' : 'descending'}`}
            >
              % {sortDescending ? '▼' : '▲'}
            </span>
          </div>
          {sortedLabels.map((label, index) => {
            const value = sortedData[index];
            const percentage = Math.round((value / total) * 100);
            return (
              <div key={label} className="compact-pie-table-row">
                <div className="compact-pie-table-label">
                  <div 
                    className="compact-pie-table-color" 
                    style={{ backgroundColor: sortedColors[index] }}
                  ></div>
                  <span className="compact-pie-table-text">{label}</span>
                </div>
                <span className="compact-pie-table-count">{value}</span>
                <span className="compact-pie-table-percentage">{percentage}%</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CompactPieChart;
